import React, { useState, useEffect } from 'react';

import { Grid } from '@material-ui/core';
import Fade from '@material-ui/core/Fade';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { trackEvent } from 'on_web/helpers/userTrackingEventsHelper';

import LifestyleIntro from 'on_web/components/onboarding/lifestyle/LifestyleIntro';
import LifestyleSleepStress from 'on_web/components/onboarding/lifestyle/LifestyleSleepStress';
import LifestyleSmokeDrink from 'on_web/components/onboarding/lifestyle/LifestyleSmokeDrink';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';

import {
  ROUTE_INTRO,
  ROUTE_ONBOARDING_GENERAL,
  ROUTE_ONBOARDING_DISEASES,
  ROUTE_HEALTH_PROFILE,
} from 'routes';
import { getRedirectRoute } from '../helpers/redirectHelper';

import UserProfileContext from 'on_web/context/userProfileContext';
import useUsersApi from '../hooks/useUsersApi';

function OnboardingLifestylePage(props) {
  const context = React.useContext(UserProfileContext);
  const [
    {
      response,
      responseCovidLifestyle,
      loading,
      getUserData,
      saveUserData,
      getCovidLifestyle,
      saveCovidLifestyle,
    },
  ] = useUsersApi();
  const [editMode, setEditMode] = useState(false);

  const [visibleStep, setVisibleStep] = useState(
    props.location.state ? null : 0
  );
  const [hideWelcome, setHideWelcome] = useState(true);

  useEffect(() => {
    let covidLifestyle;
    getCovidLifestyle()
      .then(response => {
        covidLifestyle = response;
        return getUserData();
      })
      .then(response => {
        if (response != null) {
          if (props.location.state && props.location.state.editMode) {
            // if edit mode is requested, jump to requested step
            setEditMode(true);
            setVisibleStep(props.location.state.editStep);
            return;
          } else {
            setEditMode(false);
          }

          if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromLast
          ) {
            return setVisibleStep(2);
          } else if (Object.keys(response.user_metadata).length === 0) {
            props.history.push(ROUTE_INTRO);
          } else if (
            covidLifestyle['sleep_level'] == null ||
            covidLifestyle['stress_level'] == null ||
            covidLifestyle['exercise_level'] == null
          ) {
            setVisibleStep(0);
          } else if (
            visibleStep == null &&
            props.location.state &&
            props.location.state.startFromFirst
          ) {
            setVisibleStep(1);
          } else if (
            covidLifestyle['smoking_level'] == null ||
            covidLifestyle['drinking_level'] == null
          ) {
            setVisibleStep(2);
          } else {
            const route = getRedirectRoute(
              response.user_metadata,
              context.showBasicQuestions
            );
            props.history.push(route);
          }
        }
      });
  }, [getUserData, getCovidLifestyle]);

  const onWelcome = () => {
    // when intro is displayed, it is time for another step
    setHideWelcome(false);
  };

  const handleNextClick = covidLifestyle => {
    if (covidLifestyle) {
      saveCovidLifestyle(covidLifestyle).then(resp => {
        if (resp != null) {
          saveUserMetaData();
        }
      });
    } else {
      saveUserMetaData();
    }
  };

  const saveUserMetaData = () => {
    if (visibleStep + 1 > 2) {
      response.user_metadata.onboarding = {
        ...response.user_metadata.onboarding,
        lifestyleFinished: true,
      };
      saveUserData(response).then(resp => {
        if (resp != null) {
          trackEvent(
            'Completed LIFESTYLE',
            'User has completed LIFESTYLE section onboarding'
          );

          if (editMode) {
            props.history.push(ROUTE_HEALTH_PROFILE);
          } else {
            props.history.push({
              pathname: ROUTE_ONBOARDING_DISEASES,
              state: { startFromFirst: true },
            });
          }
        }
      });
    } else {
      saveUserData(response).then(resp => {
        if (resp != null) {
          if (editMode) {
            props.history.push(ROUTE_HEALTH_PROFILE);
          } else {
            setVisibleStep(visibleStep + 1);
          }
        }
      });
    }
  };

  return (
    <div>
      <Grid
        container={useMediaQuery('(min-width:1000px)')}
        direction="row"
        justify="center"
        alignItems="center"
      >
        {loading === false && response && visibleStep != null && (
          <div>
            <UserProfileContext.Provider
              value={{
                userMetaData: response.user_metadata,
                covidLifestyle: responseCovidLifestyle,
                showBasicQuestions:
                  context && context.showBasicQuestions === true
                    ? context.showBasicQuestions
                    : false,
                editMode: editMode,
              }}
            >
              {visibleStep === 0 && (
                <PageItemContainer>
                  <Fade
                    in={hideWelcome}
                    onExited={() => setVisibleStep(visibleStep + 1)}
                  >
                    <LifestyleIntro onWelcome={() => onWelcome()} />
                  </Fade>
                </PageItemContainer>
              )}
              {visibleStep === 1 && (
                <PageItemContainer>
                  <LifestyleSleepStress
                    onClickNext={userMetaData => handleNextClick(userMetaData)}
                    onClickBack={() =>
                      props.history.push({
                        pathname: ROUTE_ONBOARDING_GENERAL,
                        state: {
                          startFromLast: true,
                        },
                      })
                    }
                  />
                </PageItemContainer>
              )}
              {visibleStep === 2 && (
                <PageItemContainer>
                  <LifestyleSmokeDrink
                    onClickNext={userMetaData => handleNextClick(userMetaData)}
                    onClickBack={() => setVisibleStep(visibleStep - 1)}
                  />
                </PageItemContainer>
              )}
            </UserProfileContext.Provider>
          </div>
        )}
      </Grid>
    </div>
  );
}

export default OnboardingLifestylePage;
